import React, { useRef, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import emailjs from '@emailjs/browser';
import { FiCheckCircle, FiClock } from 'react-icons/fi';
import './Contact.css';

import { 
  FaEnvelope, 
  FaLinkedinIn, 
  FaFacebookF, 
  FaInstagram,
  FaGithub,
} from 'react-icons/fa';

const Contact = () => {
  const form = useRef();
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [lastSubmission, setLastSubmission] = useState(() => {
    // Get last submission time from localStorage
    const savedTime = localStorage.getItem('lastSubmission');
    return savedTime ? parseInt(savedTime, 10) : 0;
  });

  useEffect(() => {
    emailjs.init("XOfsxBX6Vh3mivUh0");
  }, []);

  const sendEmail = (e) => {
    e.preventDefault();
    setIsSending(true);

    // 3-hour rate limiting (3 hours = 10800000 milliseconds)
    const currentTime = Date.now();
    const timeSinceLast = currentTime - lastSubmission;

    if (timeSinceLast < 10800000) {
      const remainingTime = 10800000 - timeSinceLast;
      const hours = Math.floor(remainingTime / 3600000);
      const minutes = Math.floor((remainingTime % 3600000) / 60000);
      
      setNotificationMessage(
        `Please wait ${hours}h ${minutes}m before sending another message`
      );
      setIsSuccess(false);
      setShowNotification(true);
      setIsSending(false);
      setTimeout(() => setShowNotification(false), 5000);
      return;
    }

    if (!form.current.checkValidity()) {
      setNotificationMessage('Please fill out all required fields correctly.');
      setIsSuccess(false);
      setShowNotification(true);
      setIsSending(false);
      setTimeout(() => setShowNotification(false), 5000);
      return;
    }

    emailjs
      .sendForm(
        'service_8xvnu0k',
        'template_67sgz6q',
        form.current,
        'XOfsxBX6Vh3mivUh0'
      )
      .then(
        (result) => {
          setNotificationMessage('Message sent successfully! I\'ll respond shortly.');
          setIsSuccess(true);
          setShowNotification(true);
          form.current.reset();
          const now = Date.now();
          setLastSubmission(now);
          localStorage.setItem('lastSubmission', now.toString());
          setTimeout(() => setShowNotification(false), 5000);
        },
        (error) => {
          setNotificationMessage(`Failed to send message: ${error.text}`);
          setIsSuccess(false);
          setShowNotification(true);
          setTimeout(() => setShowNotification(false), 5000);
        }
      )
      .finally(() => setIsSending(false));
  };

  return (
    <motion.section
      id="contact"
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 1 }}
      className="contact"
    >
      <h2>Get in Touch</h2>
      <form ref={form} onSubmit={sendEmail}>
        <input 
          type="text" 
          name="from_name" 
          placeholder="Your Name" 
          required 
          className="form-input"
        />
        <input 
          type="email" 
          name="from_email" 
          placeholder="Your Email Address" 
          required 
          className="form-input"
        />
        <textarea 
          name="message" 
          placeholder="How can I help you?" 
          rows="5" 
          required 
          className="form-textarea"
        ></textarea>
        
        <motion.button
          type="submit"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          disabled={isSending}
          className="submit-btn"
        >
          {isSending ? 'Sending...' : 'Send Message'}
        </motion.button>

        <div className="social-contacts">
                  <h3>connect with me on</h3>
                  <div className="social-icons">
                    <a href="mailto:sanojshrestha2000@gmail.com" target="_blank" rel="noopener noreferrer" className="social-icon">
                      <FaEnvelope />
                      <span className="tooltip">Email: sanojshrestha2000@gmail.com</span>
                    </a>
                    <a href="https://www.linkedin.com/in/sanoj-shrestha-3840351b8/" target="_blank" rel="noopener noreferrer" className="social-icon">
                      <FaLinkedinIn />
                      <span className="tooltip">LinkedIn:Sanoj Shrestha</span>
                    </a>
                    <a href="https://www.facebook.com/sanoj.shrestha.50552" target="_blank" rel="noopener noreferrer" className="social-icon">
                      <FaFacebookF />
                      <span className="tooltip">Facebook: Sanoj Shrestha</span>
                    </a>
                    <a href="https://www.instagram.com/sanoj_stha/" target="_blank" rel="noopener noreferrer" className="social-icon">
                      <FaInstagram />
                      <span className="tooltip">Instagram: sanoj_stha</span>
                    </a>
                    <a href="https://github.com/yourusername" target="_blank" rel="noopener noreferrer" className="social-icon">
                      <FaGithub />
                      <span className="tooltip">GitHub: yourusername</span>
                    </a>
                  </div>
                </div>
      </form>

      <AnimatePresence>
        {showNotification && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className={`notification ${isSuccess ? 'success' : 'error'}`}
          >
            {isSuccess ? (
              <FiCheckCircle className="notification-icon" />
            ) : (
              <FiClock className="notification-icon" />
            )}
            {notificationMessage}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.section>
  );
};

export default Contact;